/**
 * @file Shared React component — components/common/MaterialCard.tsx.
 */

import React from "react";
import "./MaterialCard.css";
import Button from "./Button";
import { resolveUploadAssetUrl } from "../../utils/resolveUploadAssetUrl";

const MATERIAL_IMAGE_PLACEHOLDER =
  "https://cdn-icons-png.flaticon.com/512/888/888879.png";

interface MaterialCardProps {
  /** Material ID */
  id: number;
  /** Material name */
  name: string;
  /** Material code */
  code: string;
  /** Material image URL */
  imageUrl?: string | null;
  /** Statistics data */
  stats?: {
    allDatasets?: number;
    sampleSize?: number;
    average?: number;
    min?: number;
    max?: number;
    standardDeviation?: number;
    /**
     * @deprecated Unit is now displayed inline (no separate Unit field).
     * Kept for backward compatibility with existing callers.
     */
    unit?: string;
  };
  /** Click handler for explore button */
  onExplore?: (materialId: number) => void;
  /**
   * Controls the "Explore Datapool" button visibility.
   * Defaults to true so existing usages are unchanged.
   */
  showExploreButton?: boolean;
  /**
   * Controls the "All Datasets (N)" stat visibility.
   * Defaults to true so existing usages are unchanged.
   */
  showAllDatasetsStat?: boolean;
  /**
   * Controls the statistics panel visibility.
   * Defaults to true so existing usages are unchanged.
   */
  showStats?: boolean;
  /** Optional runtime API base (`http://host:5050/api`) for upload URL resolution. */
  apiBaseUrl?: string | null;
}

/**
 * Reusable Material Card component
 * Displays material information with statistics
 */
const MaterialCard: React.FC<MaterialCardProps> = ({
  id,
  name,
  code,
  imageUrl,
  stats,
  onExplore,
  showExploreButton = true,
  showAllDatasetsStat = true,
  showStats = true,
  apiBaseUrl,
}) => {
  const fullImageUrl =
    resolveUploadAssetUrl(imageUrl, apiBaseUrl) || MATERIAL_IMAGE_PLACEHOLDER;

  const DISPLAY_UNIT = "kg CO₂eq/kg";
  const formatWithUnit = (value?: number) => {
    if (value === null || value === undefined) return "-";
    return `${value} ${DISPLAY_UNIT}`;
  };

  const defaultStats = {
    allDatasets: 0,
    sampleSize: 0,
    average: 0,
    min: 0,
    max: 0,
    standardDeviation: 0,
  };

  const displayStats = stats || defaultStats;

  return (
    <div className="material-card">
      {/* Row: Two Sections */}
      <div className="material-content-row">
        {/* Section 1: Image and Material Name */}
        <div className="material-left-section">
          <div className="material-image-wrapper">
            <img
              src={fullImageUrl}
              alt={name}
              onError={(e) => {
                e.currentTarget.src = MATERIAL_IMAGE_PLACEHOLDER;
              }}
              className="material-thumb"
            />
          </div>
          <h3 className="material-name">{name}</h3>
        </div>

        {/* Section 2: Statistics Values - 3 Rows Stacked Vertically */}
        {showStats ? (
        <div className="material-right-section">
          <div className="material-stats">
            {/* Row 1: All Datasets (N), Sample Size (n), Empty Space */}
            <div className="stat-row">
              {showAllDatasetsStat ? (
                <>
                  <div className="stat-item">
                    <span className="stat-key">All Datasets (N)</span>
                    <span className="stat-value">{displayStats.allDatasets}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-key">Sample Size (n)</span>
                    <span className="stat-value">{displayStats.sampleSize}</span>
                  </div>
                  <div className="stat-item stat-item-empty"></div>
                </>
              ) : (
                <>
                  <div className="stat-item">
                    <span className="stat-key">Sample Size (n)</span>
                    <span className="stat-value">{displayStats.sampleSize}</span>
                  </div>
                  <div className="stat-item stat-item-empty"></div>
                  <div className="stat-item stat-item-empty"></div>
                </>
              )}
            </div>

            {/* Row 2: Average, Min, Max */}
            <div className="stat-row">
              <div className="stat-item">
                <span className="stat-key">Average</span>
                <span className="stat-value">{formatWithUnit(displayStats.average)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-key">Min</span>
                <span className="stat-value">{formatWithUnit(displayStats.min)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-key">Max</span>
                <span className="stat-value">{formatWithUnit(displayStats.max)}</span>
              </div>
            </div>

            {/* Row 3: Standard Deviation */}
            <div className="stat-row">
              <div className="stat-item">
                <span className="stat-key">Standard Deviation</span>
                <span className="stat-value">{formatWithUnit(displayStats.standardDeviation)}</span>
              </div>
              <div className="stat-item stat-item-empty"></div>
              <div className="stat-item stat-item-empty"></div>
            </div>
          </div>
        </div>
        ) : null}
      </div>

      {/* Button at Bottom */}
      {showExploreButton && (
        <div className="material-footer">
          <Button
            label="Explore Datapool"
            onClick={() => onExplore?.(id)}
            type="button"
          />
        </div>
      )}
    </div>
  );
};

/** React component — Material Card. */
export default MaterialCard;

